<template>
  <div class="card guide-card mb-4">
    <div class="card-header section-header border-0 pt-3 pb-0 px-3">
      <h5 class="section-title">Fluxo de Aplicacao de Defensivos</h5>
      <span class="section-badge">Guia Visual</span>
    </div>

    <div class="card-body pt-3">
      <div class="guide-top mb-3">
        <div>
          <strong class="guide-mode">Modo recomendado: {{ operationMode }}</strong>
          <small class="guide-sub">Baseado nas condicoes climaticas e boas praticas operacionais</small>
        </div>
        <div class="guide-legend">
          <span class="dot dot-ok"></span><small>Janela favoravel</small>
          <span class="dot dot-alert"></span><small>Atencao</small>
        </div>
      </div>

      <div class="timeline-wrap">
        <div class="timeline-line"></div>
        <div class="timeline-runner"></div>

        <div class="row">
          <div v-for="(step, index) in animatedSteps" :key="step.title" class="col-12 col-lg-3 mb-3">
            <div class="step-card" :class="step.levelClass" :style="{ animationDelay: `${index * 0.16}s` }">
              <div class="step-head">
                <span class="step-number">{{ index + 1 }}</span>
                <vue-feather :type="step.icon" size="15" />
              </div>
              <h6>{{ step.title }}</h6>
              <p>{{ step.description }}</p>
              <div class="equip-list">
                <span v-for="equip in step.equipments" :key="equip" class="equip-chip">{{ equip }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="weatherLoading" class="guide-loading">Atualizando fluxo conforme clima...</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExecutiveApplicationGuide',
  props: {
    weatherInsight: {
      type: Object,
      default: null,
    },
    weatherLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isWarningMode() {
      return this.weatherInsight && this.weatherInsight.level === 'warning'
    },
    operationMode() {
      if (this.weatherLoading) return 'Avaliando clima'
      if (!this.weatherInsight) return 'Padrao tecnico'
      if (this.weatherInsight.level === 'warning') return 'Aplicacao restrita e monitorada'
      if (this.weatherInsight.level === 'info') return 'Aplicacao com ajustes operacionais'
      return 'Aplicacao em janela favoravel'
    },
    animatedSteps() {
      const warning = this.isWarningMode
      return [
        {
          title: 'Pre-checagem de Campo',
          icon: 'clipboard',
          levelClass: warning ? 'step-alert' : 'step-ok',
          description: warning
            ? 'Validar vento, chuva e umidade antes de liberar frente de aplicacao.'
            : 'Confirmar condicoes de clima, alvo biologico e roteiro dos talhoes.',
          equipments: ['Estacao meteo', 'Tablet de campo', 'Checklist tecnico'],
        },
        {
          title: 'Calibracao do Pulverizador',
          icon: 'settings',
          levelClass: warning ? 'step-alert' : 'step-ok',
          description: warning
            ? 'Reforcar calibracao com foco em reducao de deriva e perdas por evaporacao.'
            : 'Ajustar vazao, pressao e taxa de aplicacao conforme recomendacao.',
          equipments: ['Pulverizador', 'Ponta anti-deriva', 'Manometro'],
        },
        {
          title: 'Aplicacao Assistida',
          icon: 'wind',
          levelClass: warning ? 'step-alert' : 'step-ok',
          description: warning
            ? 'Operar em janelas curtas com monitoramento continuo e pausas preventivas.'
            : 'Executar aplicacao com cobertura uniforme e trilha de rastreabilidade.',
          equipments: ['GPS de barra', 'Controlador de taxa', 'EPI completo'],
        },
        {
          title: 'Pos-aplicacao e Registro',
          icon: 'file-text',
          levelClass: 'step-ok',
          description:
            'Registrar operacao, validar eficiencia e emitir receita/relatorio para auditoria tecnica.',
          equipments: ['App WebReceita', 'Registro fotografico', 'Assinatura digital'],
        },
      ]
    },
  },
}
</script>

<style scoped>
.guide-card {
  border: 1px solid #d8e5ec;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(9, 32, 58, 0.08);
}

.section-header {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f2b40;
}

.section-badge {
  border-radius: 999px;
  border: 1px solid #c8dae8;
  background: #f0f7fb;
  padding: 4px 10px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #2f5771;
}

.guide-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.guide-mode {
  display: block;
  color: #123b55;
}

.guide-sub {
  color: #5b7588;
}

.guide-legend {
  display: flex;
  align-items: center;
  gap: 6px;
}

.guide-legend small {
  color: #4f677b;
  margin-right: 6px;
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.dot-ok {
  background: #158b56;
}

.dot-alert {
  background: #d97b29;
}

.timeline-wrap {
  position: relative;
}

.timeline-line {
  position: absolute;
  top: 20px;
  left: 8px;
  right: 8px;
  height: 2px;
  background: linear-gradient(90deg, #178b57, #0e617d);
  opacity: 0.25;
}

.timeline-runner {
  position: absolute;
  top: 16px;
  left: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #0f5f7f;
  box-shadow: 0 0 0 6px rgba(15, 95, 127, 0.16);
  animation: moveRunner 4s linear infinite;
}

.step-card {
  border: 1px solid #d8e6ef;
  border-radius: 12px;
  background: #fbfeff;
  padding: 10px;
  min-height: 205px;
  opacity: 0;
  transform: translateY(8px);
  animation: revealStep 0.45s ease forwards;
}

.step-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #20506c;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #198b5a, #0f5f7f);
}

.step-card h6 {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #153d57;
  margin-bottom: 6px;
}

.step-card p {
  font-size: 12px;
  color: #3e5f76;
  min-height: 54px;
}

.equip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.equip-chip {
  font-size: 11px;
  border-radius: 999px;
  border: 1px solid #c8d9e6;
  padding: 2px 8px;
  color: #234d68;
  background: #f1f8fd;
}

.step-ok {
  border-left: 3px solid #1b8e5c;
}

.step-alert {
  border-left: 3px solid #d17b31;
  background: #fffaf5;
}

.guide-loading {
  margin-top: 8px;
  font-size: 12px;
  color: #567084;
}

@keyframes moveRunner {
  0% {
    left: 0;
  }
  100% {
    left: calc(100% - 10px);
  }
}

@keyframes revealStep {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 992px) {
  .timeline-line,
  .timeline-runner {
    display: none;
  }

  .guide-top {
    flex-direction: column;
  }

  .step-card {
    min-height: auto;
  }
}
</style>
